import { useState } from "react";
import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * BookingForm — the main "Book a first session" form.
 *
 * Rules being followed:
 *  · Uses the Readdy form endpoint returned by get_form_url, with a
 *    data-readdy-form attribute on the <form>.
 *  · Encodes as application/x-www-form-urlencoded.
 *  · Includes a hidden-via-CSS honeypot text input named "website_alt".
 *  · Builds payload from new FormData(event.currentTarget) so browser
 *    autofill values are always included.
 *  · Renders any server error message from response.meta.message /
 *    response.meta.detail, plus a spam-safe silent success branch.
 *  · Success only when http 2xx AND parsed code === "OK".
 *  · Displays feedback on the current page — no navigation.
 */

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/d95mpl57gtqne2as9ku0";

type SubmitState = "idle" | "submitting" | "success" | "error";

export default function BookingForm() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const subtitleRef = useEnterAnimation<HTMLParagraphElement>({ delay: 300 });
  const formRef = useEnterAnimation<HTMLFormElement>({ delay: 420 });

  const [state, setState] = useState<SubmitState>("idle");
  const [formError, setFormError] = useState<string>("");

  // Which chip is currently active for each single-select group
  const [service, setService] = useState<string>("Individual Therapy");
  const [format, setFormat] = useState<string>("In-Person");
  const [budget, setBudget] = useState<string>("Standard");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (state === "submitting") return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // Client-side honeypot check — silent success feedback to bots.
    const honeypotValue = (formData.get("website_alt") ?? "").toString().trim();
    if (honeypotValue !== "") {
      setState("success");
      setFormError("");
      return;
    }
    // Remove empty honeypot from the outgoing payload
    if (honeypotValue === "") {
      formData.delete("website_alt");
    }

    setState("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* keep parsed = null */
      }

      const parsedObj = (parsed as {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      }) ?? {};
      const code = parsedObj?.code;
      const metaMessage = parsedObj?.meta?.message ?? "";
      const metaDetail = parsedObj?.meta?.detail ?? "";
      const topMessage = parsedObj?.message ?? "";

      const lowerBody = responseText.toLowerCase();
      const looksLikeSpam =
        metaMessage.toLowerCase().includes("spam") ||
        metaDetail.toLowerCase().includes("spam") ||
        lowerBody.includes("form data is spam") ||
        lowerBody.includes(`"spam"`);

      if (response.ok && code === "OK" && !looksLikeSpam) {
        setState("success");
        setFormError("");
        formEl.reset();
        // Restore chip defaults so the UI reflects a clean form
        setService("Individual Therapy");
        setFormat("In-Person");
        setBudget("Standard");
      } else {
        const serverMsg =
          metaMessage || metaDetail || topMessage || responseText || "";
        setFormError(
          serverMsg
            ? `We couldn't submit your request: ${serverMsg}`
            : "We couldn't submit your request. Please try again in a moment.",
        );
        setState("error");
      }
    } catch (err) {
      setFormError(
        "We couldn't reach the server. Please check your connection and try again.",
      );
      setState("error");
    }
  };

  const chipClass = (active: boolean) =>
    `cursor-pointer rounded-full px-4 py-2 text-[11px] tracking-[0.24em] uppercase font-medium border transition-colors duration-200 ${
      active
        ? "bg-foreground-950 text-background-50 border-foreground-950"
        : "bg-background-50 text-foreground-950/75 border-foreground-950/20 hover:text-foreground-950 hover:border-foreground-950/40"
    }`;

  const inputBase =
    "w-full bg-background-50 border border-foreground-950/20 rounded-xl px-4 py-3.5 text-sm text-foreground-950 placeholder:text-foreground-950/45 focus:outline-none focus:border-foreground-950/60 transition-colors duration-200";

  const labelBase =
    "block text-[10px] tracking-[0.32em] uppercase font-medium text-foreground-950/60 mb-2";

  return (
    <section
      id="contacts-form"
      className="relative w-full bg-background-50"
      style={{ minHeight: "100vh" }}
    >
      {/* Honeypot CSS — hard-to-detect and non-inline so bots can't skip it */}
      <style>{`
        .serene-hp-field {
          position: absolute !important;
          left: -9999px !important;
          top: auto !important;
          width: 1px !important;
          height: 1px !important;
          overflow: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>

      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Contacts / 03
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 03 · Book a session
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — heading + reassurance */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
              <span
                className="font-heading"
                style={{
                  fontSize: "clamp(2.75rem, 4vw, 4rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.03em",
                  color: "oklch(var(--foreground-950) / 0.35)",
                  lineHeight: 1,
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                03
              </span>
              <span className="w-12 h-[1px] bg-foreground-950/25"></span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
                Book a session
              </span>
            </div>

            <h2
              ref={titleRef}
              className="mb-8"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.5rem, 5vw, 5rem)",
                fontWeight: 300,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
                color: "oklch(var(--foreground-950))",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              Tell us a little.
              <br />
              <span className="italic text-foreground-950/85">
                Just a little is enough.
              </span>
            </h2>

            <p
              ref={subtitleRef}
              className="italic mb-10 max-w-[420px]"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(1.05rem, 1.3vw, 1.3rem)",
                fontWeight: 300,
                lineHeight: 1.55,
                color: "oklch(var(--foreground-950) / 0.72)",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              We&rsquo;ll read every message ourselves and write back within
              one working day.
            </p>

            {/* Reassurance list */}
            <ul className="flex flex-col gap-4">
              {[
                {
                  icon: "ri-lock-line",
                  label: "Every message is confidential",
                },
                {
                  icon: "ri-time-line",
                  label: "First reply within 24 hours",
                },
                {
                  icon: "ri-user-heart-line",
                  label: "You choose which therapist",
                },
              ].map((item) => (
                <li key={item.label} className="flex items-center gap-4">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <span className="text-sm text-foreground-950/85 leading-tight">
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT — the form */}
          <div className="lg:col-span-7">
            <form
              ref={formRef}
              id="serene-booking-form"
              data-readdy-form="true"
              onSubmit={handleSubmit}
              noValidate
              className="w-full bg-background-100 rounded-2xl border border-foreground-950/10 p-6 md:p-10"
            >
              {/* Honeypot */}
              <div className="serene-hp-field" aria-hidden="true">
                <label htmlFor="serene-website-alt">
                  Please leave this field blank
                </label>
                <input
                  id="serene-website-alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                />
              </div>

              {/* Two-column: name / email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-6">
                <div>
                  <label htmlFor="booking-name" className={labelBase}>
                    Your name
                  </label>
                  <input
                    id="booking-name"
                    name="name"
                    type="text"
                    required
                    maxLength={80}
                    placeholder="How would you like us to call you?"
                    className={inputBase}
                  />
                </div>
                <div>
                  <label htmlFor="booking-email" className={labelBase}>
                    Email address
                  </label>
                  <input
                    id="booking-email"
                    name="email"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="you@example.com"
                    className={inputBase}
                  />
                </div>
              </div>

              {/* Two-column: phone / preferred contact time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-8">
                <div>
                  <label htmlFor="booking-phone" className={labelBase}>
                    Phone <span className="normal-case text-foreground-950/40">(optional)</span>
                  </label>
                  <input
                    id="booking-phone"
                    name="phone"
                    type="tel"
                    maxLength={40}
                    placeholder="+351 …"
                    className={inputBase}
                  />
                </div>
                <div>
                  <label htmlFor="booking-preferred-time" className={labelBase}>
                    Preferred time to be reached
                  </label>
                  <select
                    id="booking-preferred-time"
                    name="preferred_contact_time"
                    className={`${inputBase} appearance-none cursor-pointer pr-10`}
                    defaultValue="Anytime — I'll pick up"
                  >
                    <option>Anytime — I&rsquo;ll pick up</option>
                    <option>Weekday mornings</option>
                    <option>Weekday afternoons</option>
                    <option>Weekday evenings</option>
                    <option>Weekends only</option>
                  </select>
                </div>
              </div>

              {/* Service chips (single-select) */}
              <div className="mb-8">
                <label className={labelBase}>What kind of session?</label>
                <div className="flex flex-wrap gap-2.5">
                  {[
                    "Individual Therapy",
                    "Couples Therapy",
                    "Online Sessions",
                    "Not sure yet",
                  ].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setService(opt)}
                      className={chipClass(service === opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="session_type" value={service} />
              </div>

              {/* Format chips (single-select) */}
              <div className="mb-8">
                <label className={labelBase}>How would you like to meet?</label>
                <div className="flex flex-wrap gap-2.5">
                  {["In-Person", "Online (video)", "No preference"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFormat(opt)}
                      className={chipClass(format === opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="session_format" value={format} />
              </div>

              {/* Budget chips */}
              <div className="mb-8">
                <label className={labelBase}>
                  Fee tier <span className="normal-case text-foreground-950/40">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2.5">
                  {[
                    "Standard",
                    "Sliding scale",
                    "Prefer to discuss",
                  ].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setBudget(opt)}
                      className={chipClass(budget === opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="fee_preference" value={budget} />
              </div>

              {/* Message */}
              <div className="mb-8">
                <label htmlFor="booking-message" className={labelBase}>
                  What brings you here?{" "}
                  <span className="normal-case text-foreground-950/40">
                    (optional, up to 500 characters)
                  </span>
                </label>
                <textarea
                  id="booking-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="A sentence or two is enough. There's no wrong way to answer."
                  className={`${inputBase} resize-none`}
                ></textarea>
              </div>

              {/* Consent */}
              <div className="mb-10 flex items-start gap-3">
                <input
                  id="booking-consent"
                  name="consent"
                  type="checkbox"
                  required
                  className="mt-1 w-4 h-4 accent-foreground-950 cursor-pointer"
                  value="yes"
                />
                <label
                  htmlFor="booking-consent"
                  className="text-xs text-foreground-950/70 leading-relaxed cursor-pointer"
                >
                  I understand that Serene will use the details above only to
                  reply about my session request. Nothing else, ever.
                </label>
              </div>

              {/* Feedback banner */}
              {state === "success" ? (
                <div className="mb-6 rounded-xl border border-primary-500/40 bg-primary-500/10 p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-check-line text-base"></i>
                    </span>
                    <span
                      className="uppercase font-medium text-foreground-950"
                      style={{ fontSize: "11px", letterSpacing: "0.28em" }}
                    >
                      Message received
                    </span>
                  </div>
                  <p className="text-sm text-foreground-950/80 pl-11">
                    Thank you. We&rsquo;ll reply personally within one working
                    day.
                  </p>
                </div>
              ) : null}

              {state === "error" && formError ? (
                <div className="mb-6 rounded-xl border border-foreground-950/25 bg-background-50 p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-950/10 text-foreground-950">
                      <i className="ri-error-warning-line text-base"></i>
                    </span>
                    <span
                      className="uppercase font-medium text-foreground-950"
                      style={{ fontSize: "11px", letterSpacing: "0.28em" }}
                    >
                      Something went wrong
                    </span>
                  </div>
                  <p className="text-sm text-foreground-950/80 pl-11">
                    {formError}
                  </p>
                </div>
              ) : null}

              {/* Submit */}
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-4">
                <p className="text-xs italic text-foreground-950/55 max-w-[300px]">
                  Or write to us at hello@serene.studio if you&rsquo;d rather
                  keep it short.
                </p>
                <button
                  type="submit"
                  disabled={state === "submitting"}
                  className={`group inline-flex items-center gap-3 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5 ${
                    state === "submitting"
                      ? "bg-foreground-950/60 text-background-50 cursor-wait"
                      : "bg-foreground-950 text-background-50"
                  }`}
                >
                  <span className="text-[11px] tracking-[0.3em] uppercase font-medium">
                    {state === "submitting"
                      ? "Sending…"
                      : "Send Request"}
                  </span>
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                    <i
                      className={
                        state === "submitting"
                          ? "ri-loader-4-line text-lg animate-spin"
                          : "ri-arrow-right-up-line text-lg"
                      }
                    ></i>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}